import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@shared/api/client';
import { brand } from '@/theme/tokens';
import type { UserListItemDto } from '@shared/api/hooks/useUsers';
import type { OrderDto } from '@shared/api/hooks/useOrders';
import type { MatchSummaryDto } from '@shared/api/hooks/useMatches';
import type { PostListItemDto } from '@shared/api/hooks/usePosts';

interface SearchResult {
  id: string;
  type: 'user' | 'order' | 'match' | 'post';
  label: string;
  sublabel?: string;
  badge?: string;
  badgeTone?: 'red' | 'green' | 'gold' | 'blue' | 'default';
  path: string;
}

interface SearchGroup {
  key: string;
  label: string;
  icon: string;
  color: string;
  results: SearchResult[];
}

interface PaginatedUsers {
  data: UserListItemDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface PaginatedOrders {
  data: OrderDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface PaginatedMatches {
  data: MatchSummaryDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface PaginatedPosts {
  data: PostListItemDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

const BADGE_PALETTES = {
  red:     { bg: '#fde8e6', fg: '#AC0000' },
  green:   { bg: '#e6f4ea', fg: '#0a8f3f' },
  gold:    { bg: '#fff3dd', fg: '#8a6612' },
  blue:    { bg: '#e6eefc', fg: '#1a6fd4' },
  default: { bg: '#f0ede6', fg: '#0b0b0d' },
};

function InlineBadge({ text, tone }: { text: string; tone: keyof typeof BADGE_PALETTES }) {
  const palette = BADGE_PALETTES[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        background: palette.bg,
        color: palette.fg,
        fontSize: 9,
        fontWeight: 900,
        letterSpacing: 0.6,
        padding: '2px 6px',
        borderRadius: 3,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {text}
    </span>
  );
}

function highlight(text: string, query: string): { part: string; match: boolean }[] {
  if (!query) return [{ part: text, match: false }];
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return [{ part: text, match: false }];
  return [
    { part: text.slice(0, idx), match: false },
    { part: text.slice(idx, idx + query.length), match: true },
    { part: text.slice(idx + query.length), match: false },
  ];
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const parts = highlight(text, query);
  return (
    <>
      {parts.map((p, i) =>
        p.match ? (
          <mark
            key={i}
            style={{
              background: '#fde8e6',
              color: '#AC0000',
              borderRadius: 2,
              padding: '0 1px',
            }}
          >
            {p.part}
          </mark>
        ) : (
          <span key={i}>{p.part}</span>
        ),
      )}
    </>
  );
}

function SearchResultRow({
  result,
  query,
  onSelect,
}: {
  result: SearchResult;
  query: string;
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '9px 14px',
        background: hovered ? '#fafaf7' : '#fff',
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: '#0b0b0d',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <HighlightedText text={result.label} query={query} />
        </div>
        {result.sublabel && (
          <div
            style={{
              fontSize: 10,
              color: '#7a756c',
              marginTop: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {result.sublabel}
          </div>
        )}
      </div>
      {result.badge && (
        <InlineBadge text={result.badge} tone={result.badgeTone ?? 'default'} />
      )}
    </div>
  );
}

function mapUsers(users: UserListItemDto[]): SearchResult[] {
  return users.map((u) => ({
    id: u.id,
    type: 'user' as const,
    label: u.displayName ?? u.email,
    sublabel: u.displayName != null ? u.email : undefined,
    badge: `${u.totalPoints} pt`,
    badgeTone: 'blue' as const,
    path: '/fans',
  }));
}

function mapOrders(orders: OrderDto[]): SearchResult[] {
  return orders.map((o) => {
    const statusLabel = ((): string => {
      switch (o.status) {
        case 'PENDING':          return 'IN ATTESA';
        case 'CONFIRMED':        return 'CONFERMATO';
        case 'READY_FOR_PICKUP': return 'PRONTO';
        case 'PICKED_UP':        return 'RITIRATO';
        case 'SHIPPED':          return 'SPEDITO';
        case 'DELIVERED':        return 'CONSEGNATO';
        case 'CANCELLED':        return 'ANNULLATO';
        case 'REFUNDED':         return 'RIMBORSATO';
        default:                 return o.status;
      }
    })();

    const tone = ((): SearchResult['badgeTone'] => {
      if (o.status === 'READY_FOR_PICKUP' || o.status === 'DELIVERED' || o.status === 'PICKED_UP') return 'green';
      if (o.status === 'PENDING') return 'gold';
      if (o.status === 'CANCELLED' || o.status === 'REFUNDED') return 'red';
      return 'default';
    })();

    const sublabel =
      o.customer != null
        ? [o.customer.firstName, o.customer.lastName].filter(Boolean).join(' ') ||
          o.customer.email
        : undefined;

    return {
      id: o.id,
      type: 'order' as const,
      label: `#${o.id.slice(0, 8).toUpperCase()}`,
      sublabel,
      badge: statusLabel,
      badgeTone: tone,
      path: '/orders',
    };
  });
}

function mapMatches(matches: MatchSummaryDto[]): SearchResult[] {
  return matches.map((m) => {
    const statusLabel = ((): string => {
      switch (m.status) {
        case 'LIVE':       return 'LIVE';
        case 'SCHEDULED':  return 'PROG.';
        case 'FINISHED':   return 'FIN.';
        case 'POSTPONED':  return 'RINV.';
        case 'CANCELLED':  return 'ANNULL.';
        default:           return m.status;
      }
    })();

    const tone = ((): SearchResult['badgeTone'] => {
      if (m.status === 'LIVE')      return 'red';
      if (m.status === 'FINISHED')  return 'default';
      return 'blue';
    })();

    const datePart = (() => {
      try {
        return new Date(m.date).toLocaleDateString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      } catch {
        return '';
      }
    })();

    const sublabelParts = [m.competition, datePart].filter(Boolean);

    return {
      id: m.id,
      type: 'match' as const,
      label: `${m.homeTeamName} – ${m.awayTeamName}`,
      sublabel: sublabelParts.join(' · '),
      badge: statusLabel,
      badgeTone: tone,
      path: '/matches',
    };
  });
}

function mapPosts(posts: PostListItemDto[]): SearchResult[] {
  return posts.map((p) => {
    const isPublished = p.publishedAt != null;
    return {
      id: p.id,
      type: 'post' as const,
      label: p.title.it ?? p.title.en ?? '...',
      sublabel: p.category,
      badge: isPublished ? 'PUBB.' : 'BOZZA',
      badgeTone: isPublished ? 'green' : 'default',
      path: '/news',
    };
  });
}

function useGlobalSearch(query: string): {
  groups: SearchGroup[];
  loading: boolean;
  error: boolean;
} {
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setGroups([]);
      setLoading(false);
      setError(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError(false);

      const q = query.trim();
      const lower = q.toLowerCase();

      const [usersResult, ordersResult, matchesResult, postsResult] =
        await Promise.allSettled([
          apiClient.get<PaginatedUsers>('/users', { params: { search: q, limit: 5 } }),
          apiClient.get<PaginatedOrders>('/shop/orders', { params: { search: q, limit: 5 } }),
          apiClient.get<PaginatedMatches>('/matches', { params: { limit: 30 } }),
          apiClient.get<PaginatedPosts>('/posts', {
            params: { limit: 30, includeDrafts: true },
          }),
        ]);

      let anyError = false;

      const userResults: SearchResult[] =
        usersResult.status === 'fulfilled'
          ? mapUsers(usersResult.value.data.data)
          : (((anyError = true), []));

      const orderResults: SearchResult[] =
        ordersResult.status === 'fulfilled'
          ? mapOrders(ordersResult.value.data.data)
          : (((anyError = true), []));

      const matchResults: SearchResult[] =
        matchesResult.status === 'fulfilled'
          ? mapMatches(
              matchesResult.value.data.data
                .filter(
                  (m) =>
                    m.homeTeamName.toLowerCase().includes(lower) ||
                    m.awayTeamName.toLowerCase().includes(lower),
                )
                .slice(0, 4),
            )
          : (((anyError = true), []));

      const postResults: SearchResult[] =
        postsResult.status === 'fulfilled'
          ? mapPosts(
              postsResult.value.data.data
                .filter((p) => (p.title.it ?? '').toLowerCase().includes(lower))
                .slice(0, 4),
            )
          : (((anyError = true), []));

      const allGroups: SearchGroup[] = [
        {
          key: 'user',
          label: 'Tifosi',
          icon: '◎',
          color: brand.commerce,
          results: userResults,
        },
        {
          key: 'order',
          label: 'Ordini',
          icon: '▣',
          color: brand.gold,
          results: orderResults,
        },
        {
          key: 'match',
          label: 'Partite',
          icon: '◉',
          color: brand.primary,
          results: matchResults,
        },
        {
          key: 'post',
          label: 'News',
          icon: '▥',
          color: brand.muted,
          results: postResults,
        },
      ].filter((g) => g.results.length > 0);

      setGroups(allGroups);
      setError(anyError && allGroups.length === 0);
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  return { groups, loading, error };
}

export function GlobalSearch(): React.ReactElement {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { groups, loading } = useGlobalSearch(inputValue);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
      <div
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 4,
          padding: '7px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: 380,
          fontSize: 12,
          color: 'rgba(255,255,255,0.7)',
        }}
      >
        <span>⌕</span>
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (inputValue.length >= 2) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder="Cerca utente, ordine, partita, post..."
          style={{
            background: 'transparent',
            border: 0,
            outline: 'none',
            color: '#fff',
            flex: 1,
            fontSize: 12,
            fontFamily: 'inherit',
          }}
        />
        {loading ? (
          <span
            style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}
          >
            ...
          </span>
        ) : (
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: 1,
              background: 'rgba(255,255,255,0.1)',
              padding: '2px 6px',
              borderRadius: 3,
            }}
          >
            ⌘ K
          </span>
        )}
      </div>

      {open && inputValue.length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            width: 440,
            background: '#fff',
            border: '1px solid #E4E1D8',
            borderRadius: 8,
            boxShadow: '0 16px 40px rgba(0,0,0,0.16)',
            zIndex: 9999,
            overflow: 'hidden',
          }}
        >
          {loading && groups.length === 0 && (
            <div
              style={{
                padding: '20px 16px',
                textAlign: 'center',
                fontSize: 12,
                color: '#7a756c',
              }}
            >
              Ricerca in corso...
            </div>
          )}
          {!loading && groups.length === 0 && (
            <div
              style={{
                padding: '20px 16px',
                textAlign: 'center',
                fontSize: 12,
                color: '#7a756c',
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 14,
                  color: '#0b0b0d',
                  marginBottom: 4,
                }}
              >
                Nessun risultato
              </div>
              per &ldquo;{inputValue}&rdquo;
            </div>
          )}
          {groups.map((group, gi) => (
            <div key={group.key}>
              {gi > 0 && <div style={{ height: 1, background: '#E4E1D8' }} />}
              <div
                style={{
                  padding: '8px 14px 4px',
                  fontSize: 9,
                  fontWeight: 900,
                  letterSpacing: 1.5,
                  color: '#a8a39a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ color: group.color }}>{group.icon}</span>
                {group.label.toUpperCase()}
              </div>
              {group.results.map((result) => (
                <SearchResultRow
                  key={result.id}
                  result={result}
                  query={inputValue}
                  onSelect={() => {
                    setOpen(false);
                    setInputValue('');
                    navigate(result.path);
                  }}
                />
              ))}
            </div>
          ))}
          <div
            style={{
              padding: '8px 14px',
              borderTop: '1px solid #E4E1D8',
              fontSize: 10,
              color: '#a8a39a',
              fontWeight: 600,
            }}
          >
            ↵ seleziona · Esc chiudi
          </div>
        </div>
      )}
    </div>
  );
}
